export const INTELLIKEY_MACOS_DOWNLOAD_URL =
  process.env.REACT_APP_INTELLIKEY_MACOS_DMG_URL ||
  'https://firebasestorage.googleapis.com/v0/b/intelligentkeyboard.appspot.com/o/DMG%2FMacOS%2FIntelliKey.dmg?alt=media&token=12ed1b62-5b8e-48e4-ab9b-84eb549c802b'

export const INTELLIKEY_WINDOWS_DOWNLOAD_URL =
  process.env.REACT_APP_INTELLIKEY_WINDOWS_SETUP_URL ||
  'https://firebasestorage.googleapis.com/v0/b/intelligentkeyboard.appspot.com/o/MSI%2FWindows%2FIntelliKey.Windows.Setup.exe?alt=media&token=af47693b-f0dd-4197-b8a8-626b398c2cfe'

export const DESKTOP_RELEASE_COLLECTION = 'DesktopReleases'

const RELEASE_PLATFORMS = {
  macos: {
    fallbackUrl: INTELLIKEY_MACOS_DOWNLOAD_URL,
    storagePrefix: 'DMG/MacOS/',
    filename: 'IntelliKey.dmg'
  },
  windows: {
    fallbackUrl: INTELLIKEY_WINDOWS_DOWNLOAD_URL,
    storagePrefix: 'MSI/Windows/',
    filename: 'IntelliKey.Windows.Setup.exe'
  }
}

const releaseStoragePathFromUrl = url => {
  try {
    const parsed = new URL(url)
    const prefix = '/v0/b/intelligentkeyboard.appspot.com/o/'
    if (parsed.protocol !== 'https:' ||
        parsed.hostname !== 'firebasestorage.googleapis.com' ||
        !parsed.pathname.startsWith(prefix) ||
        parsed.searchParams.get('alt') !== 'media' ||
        !parsed.searchParams.get('token')) {
      return null
    }
    return decodeURIComponent(parsed.pathname.slice(prefix.length))
  } catch (error) {
    return null
  }
}

export const normalizePublishedDesktopRelease = (platform, release) => {
  const expected = RELEASE_PLATFORMS[platform]
  if (!expected || !release || release.published !== true ||
      release.platform !== platform ||
      !['beta', 'stable'].includes(release.channel) ||
      typeof release.version !== 'string' ||
      !/^\d+\.\d+\.\d+$/.test(release.version) ||
      typeof release.url !== 'string' ||
      typeof release.storagePath !== 'string' ||
      typeof release.sha256 !== 'string' ||
      !/^[0-9A-F]{64}$/.test(release.sha256) ||
      !Number.isSafeInteger(release.sizeBytes) ||
      release.sizeBytes < 1 ||
      typeof release.storageGeneration !== 'string' ||
      !/^\d+$/.test(release.storageGeneration)) {
    return null
  }

  const urlStoragePath = releaseStoragePathFromUrl(release.url)
  if (urlStoragePath !== release.storagePath ||
      !release.storagePath.startsWith(expected.storagePrefix) ||
      !release.storagePath.includes(`/v${release.version}/`) ||
      !release.storagePath.endsWith(`/${expected.filename}`)) {
    return null
  }

  return {
    platform,
    channel: release.channel,
    version: release.version,
    url: release.url,
    storagePath: release.storagePath,
    storageGeneration: release.storageGeneration,
    sha256: release.sha256,
    sizeBytes: release.sizeBytes
  }
}

export const loadPublishedDesktopRelease = async (firebaseLike, platform) => {
  if (!firebaseLike || typeof firebaseLike.firestore !== 'function' ||
      !RELEASE_PLATFORMS[platform]) {
    return null
  }
  const snapshot = await firebaseLike.firestore()
    .collection(DESKTOP_RELEASE_COLLECTION)
    .doc(platform)
    .get()
  if (!snapshot || !snapshot.exists) return null
  return normalizePublishedDesktopRelease(platform, snapshot.data())
}

const publishedUrlFor = (platform, desktopReleases) => {
  const release = normalizePublishedDesktopRelease(
    platform,
    desktopReleases && desktopReleases[platform]
  )
  return release ? release.url : RELEASE_PLATFORMS[platform].fallbackUrl
}

export const isMacOSBrowser = navigatorLike => {
  if (!navigatorLike) return false

  const userAgent = navigatorLike.userAgent || ''
  const legacyPlatform = navigatorLike.platform || ''
  const clientPlatform = navigatorLike.userAgentData && navigatorLike.userAgentData.platform
  const reportsIOS = /iPhone|iPad|iPod|Mobile\//i.test(userAgent) ||
    /^(iPhone|iPad|iPod)$/i.test(legacyPlatform) ||
    clientPlatform === 'iOS'
  if (reportsIOS) return false

  const reportsMac = clientPlatform === 'macOS' ||
    legacyPlatform.startsWith('Mac') ||
    /Macintosh|Mac OS X/.test(userAgent)
  const isIPadMasqueradingAsMac = Number(navigatorLike.maxTouchPoints || 0) > 1 &&
    (clientPlatform === 'macOS' || legacyPlatform.startsWith('Mac'))

  return reportsMac && !isIPadMasqueradingAsMac
}

export const isWindowsBrowser = navigatorLike => {
  if (!navigatorLike) return false

  const userAgent = navigatorLike.userAgent || ''
  const legacyPlatform = navigatorLike.platform || ''
  const clientPlatform = navigatorLike.userAgentData && navigatorLike.userAgentData.platform
  return clientPlatform === 'Windows' ||
    legacyPlatform.startsWith('Win') ||
    /Windows NT/i.test(userAgent)
}

export const desktopDownloadFor = ({
  navigatorLike,
  isMacApp,
  isWindowsApp,
  desktopReleases
}) => {
  if (isMacApp || isWindowsApp) return null

  if (isMacOSBrowser(navigatorLike)) {
    return {
      platform: 'macos',
      label: 'INTELLIKEY DESKTOP',
      url: publishedUrlFor('macos', desktopReleases)
    }
  }
  if (isWindowsBrowser(navigatorLike)) {
    return {
      platform: 'windows',
      label: 'INTELLIKEY FOR WINDOWS',
      url: publishedUrlFor('windows', desktopReleases)
    }
  }
  return null
}

export const shouldShowDesktopDownload = options =>
  desktopDownloadFor(options) !== null
